import type { AppSketch, SketchControlKind, SketchViewer } from '@api/appBuilder';
import { SKETCH_NARROW_PX } from './sketch.constants';

export interface Box {
  readonly x: number;
  readonly y: number;
  readonly w: number;
  readonly h: number;
}

export interface SketchLayout {
  readonly narrow: boolean;
  readonly frame: Box;
  readonly viewers: readonly { readonly kind: SketchViewer; readonly box: Box }[];
  readonly heading: Box;
  readonly controls: readonly { readonly kind: SketchControlKind; readonly label: string; readonly box: Box }[];
  readonly exportPill: Box;
}

const PAD = 24;
/** Leaves the Stop and Skip buttons a row of their own above the drawing. */
const PAD_TOP = 60;
const INSET = 16;
const GAP = 16;
const ROW_PX = 58;
const NARROW_ROW_PX = 52;
const HEADING_PX = 28;
const EXPORT_W = 110;
const EXPORT_H = 32;

const viewerBoxes = (area: Box, count: number, stacked: boolean): Box[] => {
  if (count < 2) return [area];
  if (stacked) {
    const h = (area.h - GAP) / 2;
    return [
      { ...area, h },
      { ...area, y: area.y + h + GAP, h },
    ];
  }
  const w = (area.w - GAP) / 2;
  return [
    { ...area, w },
    { ...area, x: area.x + w + GAP, w },
  ];
};

/** Mirrors the App Kit workspace layout; controls that don't fit are left out of the drawing. */
export const sketchLayout = (width: number, height: number, sketch: AppSketch): SketchLayout => {
  const narrow = width < SKETCH_NARROW_PX;
  const frame: Box = { x: PAD, y: PAD_TOP, w: Math.max(0, width - 2 * PAD), h: Math.max(0, height - PAD_TOP - PAD) };
  const inner: Box = { x: frame.x + INSET, y: frame.y + INSET, w: frame.w - 2 * INSET, h: frame.h - 2 * INSET };
  const rowPx = narrow ? NARROW_ROW_PX : ROW_PX;

  let viewerArea: Box;
  let column: Box;
  if (narrow) {
    const viewerH = Math.round(inner.h * 0.45);
    viewerArea = { ...inner, h: viewerH };
    column = { ...inner, y: inner.y + viewerH + GAP, h: inner.h - viewerH - GAP };
  } else {
    const columnW = Math.min(320, Math.max(200, Math.round(frame.w * 0.32)));
    column = { x: inner.x + inner.w - columnW, y: inner.y, w: columnW, h: inner.h };
    viewerArea = { ...inner, w: inner.w - columnW - GAP };
  }

  const heading: Box = { x: column.x, y: column.y, w: column.w, h: HEADING_PX };
  const room = Math.max(0, Math.floor((column.h - HEADING_PX - EXPORT_H - GAP) / rowPx));
  const controls = sketch.controls.slice(0, room).map((control, index) => ({
    ...control,
    box: { x: column.x, y: column.y + HEADING_PX + index * rowPx, w: column.w, h: rowPx },
  }));
  const exportY = column.y + HEADING_PX + controls.length * rowPx + GAP / 2;

  return {
    narrow,
    frame,
    viewers: viewerBoxes(viewerArea, sketch.viewers.length, narrow).map((box, index) => ({
      kind: sketch.viewers[index] ?? 'generic',
      box,
    })),
    heading,
    controls,
    exportPill: { x: column.x, y: exportY, w: EXPORT_W, h: EXPORT_H },
  };
};
